import { dedupeTestLMS, matchIscrittiPresenza } from "./parseReportIF";
import { calcolaDurataEventoSecondi, secondiInHHMMSS } from "./parseReportPresenze";
import * as XLSXRead from "xlsx";
import * as XLSX from "sheetjs-style";

const SOGLIA_STABILE_PCT = 5; // scarto entro cui il trend è considerato "stabile"

// Colonne riconosciute nel file iscritti (intestazioni confrontate case-insensitive, spazi e a capo
// collassati). Oltre a nome/cognome/email, gli export anagrafici del cliente portano campi
// organizzativi (area, regione, agenzia, qualifica...) che vengono salvati sull'iscritto.
const COLONNE_ISCRITTI = {
  firstName: ["first name", "firstname", "nome"],
  lastName: ["last name", "lastname", "cognome"],
  email: ["email", "e-mail", "mail", "indirizzo email", "indirizzo e-mail"],
  area: ["int", "area"],
  codiceTerzi: ["cd terzi", "cod terzi", "codice terzi"],
  regione: ["regione"],
  agenzia: ["agenzia"],
  qualifica: ["qual per sintesi", "qualifica"],
  polo: ["polo"],
  codiceFiscale: ["cod. fisc.", "cod fisc", "codice fiscale", "cf"],
};

// Campi anagrafici aggiuntivi salvati sull'iscritto (il codice fiscale è a parte, vedi anagraficaRiservata.js)
export const CAMPI_EXTRA_ISCRITTO = [
  { key: "area", label: "Area" },
  { key: "regione", label: "Regione" },
  { key: "agenzia", label: "Agenzia" },
  { key: "qualifica", label: "Qualifica" },
  { key: "polo", label: "Polo" },
  { key: "codiceTerzi", label: "Cod. terzi" },
];

function pulisci(v) {
  return String(v ?? "").replace(/\s+/g, " ").trim();
}

// Converte le righe grezze del file iscritti nel formato roster del corso.
// I campi assenti dal file non compaiono nell'oggetto, così un re-import con un file
// più povero non cancella i dati già presenti sull'iscritto.
export function righeIscrittiCorso(righe) {
  if (!righe.length) return [];
  const colonne = Object.keys(righe[0]);
  const mappa = {};
  Object.entries(COLONNE_ISCRITTI).forEach(([campo, alias]) => {
    const trovata = colonne.find((c) => alias.includes(pulisci(c).toLowerCase()));
    if (trovata) mappa[campo] = trovata;
  });
  if (!mappa.email) {
    throw new Error(`Nel file iscritti non trovo la colonna email. Colonne presenti: ${colonne.map(pulisci).join(", ")}`);
  }
  return righe.map((r) => {
    const out = {};
    Object.entries(mappa).forEach(([campo, colonna]) => { out[campo] = pulisci(r[colonna]); });
    out.email = out.email.toLowerCase();
    if (out.qualifica) out.qualifica = out.qualifica.toUpperCase(); // il file mescola "ProAccount"/"PROACCOUNT"
    if (out.codiceFiscale) out.codiceFiscale = out.codiceFiscale.toUpperCase();
    return out;
  }).filter((r) => r.email);
}

// Legge un file Iscritti XLSX e lo converte nel formato roster del corso
export function leggiIscrittiXlsx(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSXRead.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        resolve(righeIscrittiCorso(XLSXRead.utils.sheet_to_json(sheet, { defval: "" })));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Separa il codice fiscale dal resto: il documento corsi/{id} è leggibile dal link del report
// cliente, quindi il CF va salvato a parte (anagraficaRiservata.js). Restituisce le righe senza
// CF e la mappa email -> CF.
export function separaCodiciFiscali(righe) {
  const codiciFiscali = {};
  const senzaCf = righe.map(({ codiceFiscale, ...resto }) => {
    if (codiceFiscale) codiciFiscali[resto.email] = codiceFiscale;
    return resto;
  });
  return { righe: senzaCf, codiciFiscali };
}

// Unisce le righe importate al roster attuale (chiave: email). Gli iscritti già presenti vengono
// aggiornati con i campi del file mantenendo date di iscrizione/uscita; i nuovi entrano con
// dataIscrizione null. Chi è nel roster ma non nel file non viene toccato: può essere un'uscita
// o un file parziale, la decisione resta manuale.
export function unisciIscritti(attuale, importati) {
  const perEmail = new Map(importati.map((r) => [r.email, r]));
  let aggiornati = 0;
  const assenti = [];
  const iscritti = (attuale || []).map((i) => {
    const email = normalizzaEmail(i.email);
    const r = perEmail.get(email);
    if (!r) {
      if (!i.dataRimozione) assenti.push(i);
      return i;
    }
    perEmail.delete(email);
    const unito = { ...i, ...r };
    if (Object.keys(unito).some((k) => unito[k] !== i[k])) aggiornati++;
    return unito;
  });
  const nuovi = [...perEmail.values()].map((r) => ({ ...r, dataIscrizione: null, dataRimozione: null }));
  return { iscritti: [...iscritti, ...nuovi], aggiunti: nuovi.length, aggiornati, assenti };
}

function normalizzaEmail(email) {
  return String(email || "").trim().toLowerCase();
}

// Estrae una Date da un campo che può essere: stringa, Firestore Timestamp, Date, o null
function aData(valore) {
  if (!valore) return null;
  if (typeof valore?.toDate === "function") return valore.toDate();
  const d = new Date(valore);
  return isNaN(d.getTime()) ? null : d;
}

function dataLezione(evento) {
  return aData(evento?.dataInizio || evento?.data);
}

// Costruisce le righe pronte per essere scritte in risultatiQuizLezione a partire
// dal JSON grezzo del quiz LMS, arricchendole col roster del corso.
export function costruisciRigheQuiz(record, iscrittiCorso, totaleDomande) {
  const { byEmail } = dedupeTestLMS(record);
  const rosterByEmail = new Map(
    (iscrittiCorso || []).map((i) => [normalizzaEmail(i.email), i])
  );

  const righe = [];
  byEmail.forEach((v, email) => {
    const iscritto = rosterByEmail.get(email);
    righe.push({
      email,
      nome: iscritto?.firstName || "",
      cognome: iscritto?.lastName || "",
      risposteCorrette: v.totalCorrect ?? null,
      totaleDomande: totaleDomande || null,
      dataEsecuzioneTest: v.dateCompleted ?? null,
      tentativiCaricamento: v.tentativi || 1,
    });
  });
  return righe;
}

function trendDaSequenza(primaPct, ultimaPct) {
  if (primaPct == null || ultimaPct == null) return "n/d";
  const delta = ultimaPct - primaPct;
  if (Math.abs(delta) < SOGLIA_STABILE_PCT) return "stabile";
  return delta > 0 ? "migliorato" : "peggiorato";
}

// Andamento di UNO studente su UNA lezione, attraverso tutti i caricamenti quiz effettuati nel tempo
export function calcolaAndamentoLezione(risultatiQuizStudenteLezione) {
  const serie = (risultatiQuizStudenteLezione || [])
    .filter((r) => r.risposteCorrette != null && r.totaleDomande)
    .map((r) => ({
      data: aData(r.dataEsecuzioneTest) || aData(r.dataCaricamento),
      punteggioPct: (Number(r.risposteCorrette) / Number(r.totaleDomande)) * 100,
      risposteCorrette: r.risposteCorrette,
      totaleDomande: r.totaleDomande,
      tentativiCaricamento: r.tentativiCaricamento || 1,
    }))
    .sort((a, b) => (a.data?.getTime() || 0) - (b.data?.getTime() || 0));

  if (serie.length === 0) {
    return {
      serie: [],
      punteggioMigliore: null,
      punteggioUltimo: null,
      tentativiTotaliLezione: 0,
      numeroCaricamenti: 0,
      trendLezione: "n/d",
    };
  }

  const punteggioMigliore = Math.max(...serie.map((s) => s.punteggioPct));
  const punteggioUltimo = serie[serie.length - 1].punteggioPct;
  const tentativiTotaliLezione = serie.reduce((acc, s) => acc + s.tentativiCaricamento, 0);

  return {
    serie,
    punteggioMigliore,
    punteggioUltimo,
    tentativiTotaliLezione,
    numeroCaricamenti: serie.length,
    trendLezione: serie.length < 2 ? "n/d" : trendDaSequenza(serie[0].punteggioPct, punteggioUltimo),
  };
}

// reportPresenze è alimentato dall'export Teams (ReportPresenze.js) senza alcun arricchimento:
// i partecipanti non autenticati/ospiti arrivano spesso con email vuota. Per non perderli nel
// matching per email coi KPI, riusiamo lo stesso fuzzy-match per nome già usato da Report IF
// (matchIscrittiPresenza) per risalire all'iscritto corretto quando l'email manca.
export function arricchisciPresenzeConEmail(presenze, iscrittiCorso) {
  const iscrittiFormatoIF = (iscrittiCorso || []).map((i) => ({
    "First Name": i.firstName,
    "Last Name": i.lastName,
    "Email": i.email,
  }));
  const tutte = presenze || [];
  const conNome = tutte.filter((p) => p.nome);
  const senzaNome = tutte.filter((p) => !p.nome);
  return [...matchIscrittiPresenza(conNome, iscrittiFormatoIF), ...senzaNome];
}

// Lezioni (eventi) già svolte e comprese nella finestra di iscrizione di uno studente.
// Le lezioni future non contano nel calcolo di costanza/KPI: non essendo ancora state
// svolte non possono avere presenza/quiz, e conteggiarle abbasserebbe artificialmente
// la costanza finché non arriva la loro data.
function lezioniPertinenti(eventiOrdinati, iscritto) {
  const dataIscrizione = aData(iscritto.dataIscrizione);
  const dataRimozione = aData(iscritto.dataRimozione);
  const oggi = new Date();
  return eventiOrdinati.filter((ev) => {
    const d = dataLezione(ev);
    if (!d) return false;
    if (d > oggi) return false;
    if (dataIscrizione && d < dataIscrizione) return false;
    if (dataRimozione && d > dataRimozione) return false;
    return true;
  });
}

// Durata "di riferimento" (100%) di una lezione: quella programmata sull'evento se disponibile,
// altrimenti la permanenza più lunga osservata tra i partecipanti di quella lezione. Su eventi
// storici/migrati capita che oraInizio/oraFine non siano mai stati compilati: senza questo
// fallback la % di presenza risulterebbe sempre vuota pur avendo i minuti di presenza registrati.
function calcolaDurateRiferimento(eventi, presenze) {
  const riferimenti = new Map();
  (eventi || []).forEach((ev) => {
    const programmata = calcolaDurataEventoSecondi(ev);
    if (programmata) { riferimenti.set(ev.id, programmata); return; }
    const secondiLezione = (presenze || [])
      .filter((p) => p.eventoId === ev.id)
      .map((p) => p.durataSecondi ?? p.durataMinuti * 60)
      .filter((s) => s != null);
    riferimenti.set(ev.id, secondiLezione.length > 0 ? Math.max(...secondiLezione) : null);
  });
  return riferimenti;
}

// KPI aggregati per ogni studente del corso
export function calcolaKpiStudenti(eventi, presenzeGrezze, risultatiQuiz, iscritti) {
  const eventiOrdinati = [...(eventi || [])].sort(
    (a, b) => (dataLezione(a)?.getTime() || 0) - (dataLezione(b)?.getTime() || 0)
  );
  const presenze = arricchisciPresenzeConEmail(presenzeGrezze, iscritti);
  const durateRiferimento = calcolaDurateRiferimento(eventiOrdinati, presenze);
  // Lezioni/quiz indipendenti per cui ESISTE almeno un risultato caricato da qualcuno nel corso:
  // sono le uniche "opportunità di quiz" reali. Le altre lezioni (senza quiz per nessuno) non
  // vanno contate contro chi non le ha svolte, altrimenti la media si abbasserebbe ingiustamente
  // per tutti quando una lezione semplicemente non prevede un test.
  const eventiConQuizNelCorso = new Set((risultatiQuiz || []).map((r) => r.eventoId));

  return (iscritti || []).map((iscritto) => {
    const email = normalizzaEmail(iscritto.email);
    const attivo = !iscritto.dataRimozione;
    const lezioni = lezioniPertinenti(eventiOrdinati, iscritto);

    const andamentoLezioni = lezioni.map((ev) => {
      const presenza = (presenze || []).find(
        (p) => p.eventoId === ev.id && normalizzaEmail(p.email) === email
      );
      const quizLezione = (risultatiQuiz || []).filter(
        (r) => r.eventoId === ev.id && normalizzaEmail(r.email) === email
      );
      const durataMax = durateRiferimento.get(ev.id);
      const presenzaSecondi = presenza ? (presenza.durataSecondi ?? presenza.durataMinuti * 60) : null;
      // Se la durata di riferimento è nota, un'assenza vale 0% (non viene esclusa dalla media):
      // altrimenti la media di presenza risulterebbe calcolata solo sulle lezioni frequentate,
      // rendendo le percentuali di studenti diversi non confrontabili tra loro (vedi mediaRisposteCorrettePct).
      const presenzaPct = durataMax ? Math.min(100, ((presenzaSecondi || 0) / durataMax) * 100) : null;
      return {
        evento: ev,
        presenzaSecondi,
        presenzaPct,
        ...calcolaAndamentoLezione(quizLezione),
      };
    });

    // I quiz indipendenti (senza lezione associata, evento.standalone) non hanno una presenza
    // possibile: contano per i KPI sul quiz (sotto), ma vanno esclusi dal calcolo di costanza/
    // presenza media, altrimenti risulterebbero sempre "non frequentati" abbassandola artificialmente.
    const lezioniReali = lezioni.filter((ev) => !ev.standalone);
    const andamentoReale = andamentoLezioni.filter((l) => !l.evento.standalone);
    const lezioniConPresenza = andamentoReale.filter((l) => l.presenzaSecondi != null);
    const lezioniConPct = andamentoReale.filter((l) => l.presenzaPct != null);
    const lezioniConQuiz = andamentoLezioni.filter((l) => l.punteggioMigliore != null);

    const costanzaPresenza = lezioniReali.length > 0 ? (lezioniConPresenza.length / lezioniReali.length) * 100 : null;
    const presenzaMediaPct = lezioniConPct.length > 0
      ? lezioniConPct.reduce((acc, l) => acc + l.presenzaPct, 0) / lezioniConPct.length
      : null;

    // Media punteggio calcolata sul totale delle opportunità di quiz (non sui soli quiz svolti):
    // un quiz non svolto vale 0, così due studenti con partecipazione diversa non risultano
    // artificialmente allo stesso livello (es. 3 quiz su 13 vs 11 su 13 con la stessa media).
    const lezioniConQuizDisponibile = andamentoLezioni.filter((l) => eventiConQuizNelCorso.has(l.evento.id));
    const quizTotali = lezioniConQuizDisponibile.length;
    const mediaRisposteCorrettePct = quizTotali > 0
      ? lezioniConQuizDisponibile.reduce((acc, l) => acc + (l.punteggioMigliore ?? 0), 0) / quizTotali
      : null;
    const tentativiTotali = andamentoLezioni.reduce((acc, l) => acc + (l.tentativiTotaliLezione || 0), 0);

    const puntegiPerTrend = lezioniConQuiz.map((l) => l.punteggioMigliore);
    const trendCorso = puntegiPerTrend.length < 2
      ? "n/d"
      : trendDaSequenza(puntegiPerTrend[0], puntegiPerTrend[puntegiPerTrend.length - 1]);

    return {
      email: iscritto.email,
      nome: iscritto.firstName,
      cognome: iscritto.lastName,
      ...Object.fromEntries(CAMPI_EXTRA_ISCRITTO.map((c) => [c.key, iscritto[c.key] || ""])),
      attivo,
      dataIscrizione: iscritto.dataIscrizione,
      dataRimozione: iscritto.dataRimozione,
      lezioniTotali: lezioniReali.length,
      lezioniFrequentate: lezioniConPresenza.length,
      costanzaPresenza,
      presenzaMediaPct,
      quizSvolti: lezioniConQuiz.length,
      quizTotali,
      mediaRisposteCorrettePct,
      tentativiTotali,
      trendCorso,
      andamentoLezioni,
    };
  });
}

// Iscritti per cui una data lezione è pertinente (iscritti al momento in cui si è svolta),
// speculare a lezioniPertinenti ma nella direzione opposta (lezione -> iscritti invece di iscritto -> lezioni).
function iscrittiPertinentiALezione(iscritti, dataLezioneEv) {
  if (!dataLezioneEv) return [];
  return (iscritti || []).filter((i) => {
    const dataIscrizione = aData(i.dataIscrizione);
    const dataRimozione = aData(i.dataRimozione);
    if (dataIscrizione && dataLezioneEv < dataIscrizione) return false;
    if (dataRimozione && dataLezioneEv > dataRimozione) return false;
    return true;
  });
}

// Serie temporale a livello di corso (per i grafici aggregati): media presenza % e media miglior punteggio % per lezione.
// Le medie sono calcolate sul totale degli iscritti pertinenti a ciascuna lezione (chi non partecipa/non fa
// il quiz vale 0), non solo su chi ha effettivamente partecipato: altrimenti una lezione con pochi presenti
// tutti al 100% risulterebbe "media 100%" invece di riflettere la scarsa partecipazione — la stessa distorsione
// corretta a livello di singolo studente in calcolaKpiStudenti.
export function calcolaKpiCorso(eventi, presenzeGrezze, risultatiQuiz, iscritti) {
  const eventiOrdinati = [...(eventi || [])].sort(
    (a, b) => (dataLezione(a)?.getTime() || 0) - (dataLezione(b)?.getTime() || 0)
  );
  const presenze = arricchisciPresenzeConEmail(presenzeGrezze, iscritti);
  const durateRiferimento = calcolaDurateRiferimento(eventiOrdinati, presenze);
  const eventiConQuizNelCorso = new Set((risultatiQuiz || []).map((r) => r.eventoId));

  return eventiOrdinati.map((ev) => {
    const iscrittiLezione = iscrittiPertinentiALezione(iscritti, dataLezione(ev));

    let presenzaMediaPct = null;
    if (!ev.standalone) {
      const durataMax = durateRiferimento.get(ev.id);
      if (durataMax && iscrittiLezione.length > 0) {
        const presenzeLezione = (presenze || []).filter((p) => p.eventoId === ev.id);
        const presenzePct = iscrittiLezione.map((i) => {
          const email = normalizzaEmail(i.email);
          const presenza = presenzeLezione.find((p) => normalizzaEmail(p.email) === email);
          const secondi = presenza ? (presenza.durataSecondi ?? presenza.durataMinuti * 60) : 0;
          return Math.min(100, ((secondi || 0) / durataMax) * 100);
        });
        presenzaMediaPct = presenzePct.reduce((a, b) => a + b, 0) / presenzePct.length;
      }
    }

    let punteggioMedioPct = null;
    if (eventiConQuizNelCorso.has(ev.id) && iscrittiLezione.length > 0) {
      const quizLezione = (risultatiQuiz || []).filter((r) => r.eventoId === ev.id);
      const punteggi = iscrittiLezione.map((i) => {
        const email = normalizzaEmail(i.email);
        const righeStudente = quizLezione.filter((r) => normalizzaEmail(r.email) === email);
        return calcolaAndamentoLezione(righeStudente).punteggioMigliore ?? 0;
      });
      punteggioMedioPct = punteggi.reduce((a, b) => a + b, 0) / punteggi.length;
    }

    return {
      eventoId: ev.id,
      titolo: ev.titolo || ev.nome || "",
      data: ev.dataInizio || ev.data || "",
      standalone: !!ev.standalone,
      presenzaMediaPct,
      punteggioMedioPct,
    };
  });
}

export const LABEL_TREND = {
  migliorato: "In miglioramento",
  peggiorato: "In calo",
  stabile: "Stabile",
  "n/d": "N/D",
};

// Stato colore dello studente: bordeaux (il più grave, ha priorità sugli altri) per chi ha
// disimpegno totale — 0 in costanza presenza, presenza media e quiz svolti; altrimenti dipende dal
// punteggio medio quiz: verde sopra il 60%, giallo tra 50% e 59,9%, rosso sotto il 50%. Neutro
// (nessuna evidenziazione) solo se non ci sono ancora quiz disponibili nel corso (punteggio non
// calcolabile): non si può giudicare un punteggio che non esiste ancora.
// Condiviso tra tabella a schermo ed export XLSX così da restare sempre coerenti.
export function statoStudente(s) {
  const disimpegnoTotale = s.costanzaPresenza === 0 && s.presenzaMediaPct === 0 && s.quizSvolti === 0;
  if (disimpegnoTotale) return "bordeaux";
  if (s.mediaRisposteCorrettePct == null) return "neutro";
  if (s.mediaRisposteCorrettePct > 60) return "verde";
  if (s.mediaRisposteCorrettePct >= 50) return "giallo";
  return "rosso";
}

export const LABEL_STATO = { verde: "Verde", giallo: "Giallo", rosso: "Rosso", bordeaux: "Bordeaux", neutro: "—" };
export const COLORE_STATO_HEX = { verde: "BBF7D0", giallo: "FDE68A", rosso: "FCA5A5", bordeaux: "A2123E" };

function arrotonda(v) {
  return v == null ? null : Math.round(v * 10) / 10;
}

// Workbook XLSX condiviso tra utility interna e report cliente
// Colonne anagrafiche dello studente da inserire dopo l'email: solo quelle valorizzate per
// almeno uno studente, più il codice fiscale se passato (solo export interno, mai dal report
// cliente: il CF non è nel documento pubblico del corso, vedi anagraficaRiservata.js).
function colonneAnagrafica(kpiStudenti, codiciFiscali) {
  const colonne = [];
  if (codiciFiscali && Object.keys(codiciFiscali).length) {
    colonne.push({ label: "Codice fiscale", valore: (s) => codiciFiscali[String(s.email || "").toLowerCase()] || "" });
  }
  CAMPI_EXTRA_ISCRITTO
    .filter((c) => kpiStudenti.some((s) => s[c.key]))
    .forEach((c) => colonne.push({ label: c.label, valore: (s) => s[c.key] || "" }));
  return colonne;
}

function anagraficaStudente(s, colonne) {
  return {
    "Nome": s.nome || "",
    "Cognome": s.cognome || "",
    "Email": s.email || "",
    ...Object.fromEntries(colonne.map((c) => [c.label, c.valore(s)])),
  };
}

export function costruisciWorkbookKpi(corso, kpiStudenti, { codiciFiscali } = {}) {
  const wb = XLSX.utils.book_new();
  const anagrafica = colonneAnagrafica(kpiStudenti, codiciFiscali);
  const larghezzeAnagrafica = anagrafica.map((c) => ({ wch: ["Regione", "Agenzia", "Polo"].includes(c.label) ? 24 : 16 }));

  const riepilogoData = kpiStudenti.map((s) => ({
    ...anagraficaStudente(s, anagrafica),
    "Iscrizione": s.attivo ? "Attivo" : `Uscito il ${s.dataRimozione || ""}`,
    "Lezioni pertinenti": s.lezioniTotali,
    "Lezioni frequentate": s.lezioniFrequentate,
    "Costanza presenza (%)": arrotonda(s.costanzaPresenza),
    "Presenza media (%)": arrotonda(s.presenzaMediaPct),
    "Quiz svolti": s.quizSvolti,
    "Quiz totali": s.quizTotali,
    "Punteggio medio (%)": arrotonda(s.mediaRisposteCorrettePct),
    "Tentativi totali": s.tentativiTotali,
    "Trend corso": LABEL_TREND[s.trendCorso] || s.trendCorso,
    "Stato": LABEL_STATO[statoStudente(s)],
  }));
  const ws1 = XLSX.utils.json_to_sheet(riepilogoData);
  ws1["!cols"] = [
    { wch: 18 }, { wch: 18 }, { wch: 32 }, ...larghezzeAnagrafica, { wch: 20 }, { wch: 16 },
    { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 10 },
  ];
  Object.keys(riepilogoData[0] || {}).forEach((_, cIdx) => {
    const cell = XLSX.utils.encode_cell({ r: 0, c: cIdx });
    if (ws1[cell]) ws1[cell].s = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "1F3864" } } };
  });
  // Colora l'intera riga secondo lo stesso stato mostrato a schermo, così l'export resta
  // coerente con la tabella invece di usare una soglia diversa. Stato "neutro" (nessuna
  // evidenziazione) non ha un colore associato: la riga resta senza sfondo.
  kpiStudenti.forEach((s, idx) => {
    const row = idx + 2;
    const stato = statoStudente(s);
    const rgb = COLORE_STATO_HEX[stato];
    if (!rgb) return;
    Object.keys(riepilogoData[0] || {}).forEach((_, cIdx) => {
      const cell = XLSX.utils.encode_cell({ r: row - 1, c: cIdx });
      if (ws1[cell]) {
        ws1[cell].s = {
          fill: { fgColor: { rgb } },
          ...(stato === "bordeaux" ? { font: { color: { rgb: "FFFFFF" } } } : {}),
        };
      }
    });
  });

  const dettaglioData = [];
  kpiStudenti.forEach((s) => {
    (s.andamentoLezioni || []).forEach((l) => {
      dettaglioData.push({
        ...anagraficaStudente(s, anagrafica),
        "Lezione": l.evento?.titolo || l.evento?.nome || "",
        "Data lezione": l.evento?.dataInizio || l.evento?.data || "",
        "Presenza": l.presenzaSecondi != null ? secondiInHHMMSS(l.presenzaSecondi) : "—",
        "Presenza (%)": arrotonda(l.presenzaPct),
        "Miglior punteggio (%)": arrotonda(l.punteggioMigliore),
        "Ultimo punteggio (%)": arrotonda(l.punteggioUltimo),
        "N. caricamenti test": l.numeroCaricamenti,
        "Tentativi totali": l.tentativiTotaliLezione,
        "Trend lezione": LABEL_TREND[l.trendLezione] || l.trendLezione,
      });
    });
  });
  const ws2 = XLSX.utils.json_to_sheet(dettaglioData);
  ws2["!cols"] = [
    { wch: 18 }, { wch: 18 }, { wch: 32 }, ...larghezzeAnagrafica, { wch: 28 }, { wch: 14 },
    { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 16 },
  ];
  Object.keys(dettaglioData[0] || {}).forEach((_, cIdx) => {
    const cell = XLSX.utils.encode_cell({ r: 0, c: cIdx });
    if (ws2[cell]) ws2[cell].s = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "1F3864" } } };
  });

  XLSX.utils.book_append_sheet(wb, ws1, "Riepilogo KPI");
  XLSX.utils.book_append_sheet(wb, ws2, "Dettaglio per lezione");
  return wb;
}

// opzioni.codiciFiscali (email -> CF) va passato solo dall'interfaccia interna
export function esportaKpiXlsx(corso, kpiStudenti, opzioni) {
  const wb = costruisciWorkbookKpi(corso, kpiStudenti, opzioni);
  XLSX.writeFile(wb, `KPI_${corso?.nome || "corso"}.xlsx`);
}
